/** @prettier */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import ProductCategoryTabs from '@xanadu/ui-shared/components/ProductCategoryTabs';
import Navigation from '@xanadu/ui-shared/components/navigation/Navigation';
import SportsNavBar from '@xanadu/ui-shared/components/navigation/SportsNavBar';
import { PRODUCTS, PRODUCT_LINKS } from '@xanadu/ui-shared/constants';
import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';
import { CssVariablesReaderContext } from '@xanadu/ui-shared/contexts/CssVariablesReaderContext';
import { useReduxBridge } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import { ScrollYContext } from '@xanadu/ui-shared/contexts/ScrollYContext';
import { useImprovedTranslation } from '@xanadu/ui-shared/hooks';
import { changeProduct } from '@xanadu/ui-shared/modules/application/actions';
import { toggleNavigation as toggleNavigationAct } from '@xanadu/ui-shared/modules/navigation/actions';
import navigateExternal from '@xanadu/ui-shared/utils/navigateExternal';

import { MOBILE_BREAKPOINT } from '../../constants';

import { getClientLogo, getClientString } from '../../utils';

import ExternalVertical from './ExternalVertical/ExternalVertical';

import BurgerMenuButton from './BurgerMenuButton';
import styles from './Header.module.scss';
import Search from './Search';
import SubHeader from './SubHeader';
import SubProducts from './SubProducts';
import UserButtons from './UserButtons';

const Header = ({
  isLoggedIn,
  widthSize,
  product,
  location: { pathname },
  casinoVisibilityRestricted,
  virtualsAllowed,
  toggleNavigation,
  history,
  navigationVisible,
  sidebarVisible,
  cinemaMode,
  additionalDisplayedVerticals,
}) => {
  const { t } = useImprovedTranslation();
  const scrollY = useContext(ScrollYContext);
  const getCssVariable = useContext(CssVariablesReaderContext);
  const upperContext = React.useContext(ChildrenPropsContext) || {};
  const {
    featuresFlags: {
      cashierEnabled,
      showColossus,
      promotionsEnabled,
      showPopularProducts,
      subProductsEnabled = [],
      externalProductsEnabled = [],
      displayNativeApps = true,
    } = {},
    customization: {
      Header: {
        displayExtraLinks = false,
        displaySearch = true,
        displaySettingsMenu = false,
        displaySubHeader = false,
        walletVariant = 'v1',
        childrenProps = {},
        reverseLogoDimensions = false,
        forceLongLogo = false,
        forceShortLogo = false,
      } = {},
      navigationLayout = 'isolated',
      searchPosition = 'header',
    } = {},
  } = upperContext;

  const isFirstMount = useRef(true);
  const { clientName, clientSite } = useReduxBridge() || {};

  const isMainProduct = [PRODUCTS.exchange, PRODUCTS.futures, PRODUCTS.predictions].includes(
    product
  );

  const isHomepage = pathname === '/';

  const activeProduct = PRODUCT_LINKS.find(({ id }) => id === product);

  const [floatingNavigationEnabled, setFloatingNavigationEnabled] = useState(false);

  const cssHeaderMargin = getCssVariable({ selector: '--header-margin-bottom', format: 'number' });

  const isMobile = widthSize <= MOBILE_BREAKPOINT;

  const searchDisplayedInHeader = searchPosition === 'header';
  const navigationLayoutIsolated = navigationLayout === 'isolated';
  const navigationLayoutHeader = navigationLayout === 'header';

  const showVerticalSwitcher =
    showPopularProducts &&
    isMainProduct &&
    isMobile &&
    !navigationVisible &&
    !sidebarVisible &&
    isHomepage &&
    !casinoVisibilityRestricted &&
    virtualsAllowed;

  const listenerFunction = useCallback((event) => {
    const elementClass = event.target.className;
    const navRegex = /Navigation/;
    if (!navRegex.test(elementClass)) {
      setFloatingNavigationEnabled(false);
    }
  }, []);

  const handleNavigationClick = () => {
    setFloatingNavigationEnabled((status) => !status);
  };

  useEffect(() => {
    if (floatingNavigationEnabled) {
      document.addEventListener('click', listenerFunction);
    }

    if (!floatingNavigationEnabled) {
      document.removeEventListener('click', listenerFunction);
    }
  }, [floatingNavigationEnabled]);

  useEffect(() => {
    if (isFirstMount) {
      isFirstMount.current = false;
    }
  }, []);

  const headerClass = classNames({
    [styles.header]: true,
    [styles['header--with-shadow']]: !isMobile && cssHeaderMargin && scrollY >= cssHeaderMargin,
    [styles['header--isMainProduct']]: isMainProduct,
    [styles['header--cinemaMode']]: cinemaMode,
    [styles['header--navigationLayoutIsolated']]: navigationLayoutIsolated,
  });

  const floatingNavigationClasses = {
    appear: styles['floatingNavigationContainer-appear'],
    appearActive: styles['floatingNavigationContainer-appear-active'],
    appearDone: styles['floatingNavigationContainer-appear-done'],
    enter: styles['floatingNavigationContainer-enter'],
    enterActive: styles['floatingNavigationContainer-enter-active'],
    exit: styles['floatingNavigationContainer-exit'],
    exitActive: styles['floatingNavigationContainer-exit-active'],
    exitDone: styles['floatingNavigationContainer-exit-done'],
  };

  const getShortLogoVariant = () => {
    if (forceShortLogo) {
      return true;
    }
    if (forceLongLogo) {
      return false;
    }
    return reverseLogoDimensions ? !isMobile : isMobile;
  };

  const ClientLogo = getClientLogo(clientName, clientSite, {
    short: getShortLogoVariant(),
    wrapperClassName: styles.logoWrapper,
  });

  const clientString = getClientString(clientName, clientSite);

  const mainClass = classNames({
    [styles.main]: true,
    [styles[`main--variant-${walletVariant}`]]: true,
    [styles['main--isLoggedIn']]: isLoggedIn,
  });

  const floatingNavigationButtonClass = classNames({
    [styles.floatingNavigationButton]: true,
    [styles['floatingNavigationButton--withoutMargin']]: clientName === 'easybet',
  });

  return (
    <div className={headerClass}>
      <ChildrenPropsContext.Provider
        value={{
          ...upperContext,
          childrenProps,
        }}
      >
        <div className={mainClass}>
          <div className={styles.container}>
            {navigationLayoutIsolated && (
              <div
                className={floatingNavigationButtonClass}
                role="button"
                onClick={handleNavigationClick}
                data-hook="menu-nav-btn"
              >
                <BurgerMenuButton active={floatingNavigationEnabled} />
              </div>
            )}
            <Link
              to={activeProduct.url || '/'}
              className={styles.homeLink}
              data-hook="home-logo-link"
            >
              <ClientLogo className={styles.logo} />
            </Link>
            {displaySearch && isMainProduct && searchDisplayedInHeader && (
              <span className={styles.containerSearch}>
                <Search />
              </span>
            )}
            {displayExtraLinks && (
              <div className={styles.linksContainer}>
                {cashierEnabled && (
                  <Fragment>
                    <Link
                      to={
                        isLoggedIn
                          ? '/account/safer-gambling'
                          : '/page/rules_and_regulations/responsible-gambling'
                      }
                      data-hook="rg-page"
                      className=".gtm_cta"
                    >
                      {t('Safer Gambling')}
                    </Link>
                    {displayNativeApps && (
                      <Link
                        to="/page/about/mobile-applications/"
                        data-hook="download-apps-top"
                        className=".gtm_cta"
                      >
                        {t('Download Apps')}
                      </Link>
                    )}
                  </Fragment>
                )}
                {promotionsEnabled && (
                  <Link to={'/promotions'} data-hook="promotions-top" className=".gtm_cta">
                    {t('Promotions')}
                  </Link>
                )}
              </div>
            )}
            {Boolean(subProductsEnabled.length || externalProductsEnabled.length) && !isMobile && (
              <SubProducts
                subProductsEnabled={subProductsEnabled}
                externalProductsEnabled={externalProductsEnabled}
              />
            )}
            {isMobile ||
            Boolean(subProductsEnabled.length || externalProductsEnabled.length) ? null : (
              <div className={styles.externalVSpacer}>
                {additionalDisplayedVerticals
                  .toSorted((a, b) => {
                    if (a.label > b.label) {
                      return -1;
                    }
                    return 1;
                  })
                  .map(({ label, url, icon, iconName, openInNewTab }) => (
                    <ExternalVertical
                      key={label}
                      name={label}
                      url={url}
                      icon={icon}
                      iconName={iconName}
                      onClick={navigateExternal}
                      openInNewTab={openInNewTab}
                      dataHook={`nav-${label}`}
                      showLabel
                    />
                  ))}
              </div>
            )}
          </div>
          <div className={styles.userContainer}>
            <UserButtons displaySettingsMenu={displaySettingsMenu} />
          </div>
        </div>
        {navigationLayoutIsolated && (
          <CSSTransition
            // why this work? I have no clue
            appear
            in={isFirstMount.current ? true : floatingNavigationEnabled}
            className={styles.floatingNavigationContainer}
            classNames={{ ...floatingNavigationClasses }}
            timeout={250}
          >
            <div id="floatingNavigationContainer">
              {floatingNavigationEnabled && (
                <Navigation
                  showIcons
                  showColossus={showColossus}
                  floating
                  dataHookPrefix="tab-"
                  clientString={clientString}
                />
              )}
            </div>
          </CSSTransition>
        )}
        {showVerticalSwitcher && (
          <ProductCategoryTabs
            toggleNavigation={toggleNavigation}
            history={history}
            changeProduct={changeProduct}
            showLabel={scrollY === 0}
          />
        )}
        {displaySubHeader ? (
          <div className={styles.subHeader}>
            <SubHeader
              promotionsEnabled={promotionsEnabled}
              cashierEnabled={cashierEnabled}
              isLoggedIn={isLoggedIn}
              product={product}
            />
          </div>
        ) : null}
        {isMainProduct && navigationLayoutHeader && (
          <div className={styles.subHeader}>
            <SportsNavBar />
          </div>
        )}
      </ChildrenPropsContext.Provider>
    </div>
  );
};

Header.propTypes = {
  isLoggedIn: PropTypes.bool,
  casinoVisibilityRestricted: PropTypes.bool.isRequired,
  sidebarVisible: PropTypes.bool.isRequired,
  cinemaMode: PropTypes.bool.isRequired,
  toggleNavigation: PropTypes.func.isRequired,
  virtualsAllowed: PropTypes.bool.isRequired,
  navigationVisible: PropTypes.bool.isRequired,
  widthSize: PropTypes.number.isRequired,
  product: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }),
  additionalDisplayedVerticals: PropTypes.array.isRequired,
};

const mapDispatchToProps = {
  toggleNavigation: toggleNavigationAct,
};

function mapStateToProps(state) {
  return {
    widthSize: state.getIn(['application', 'widthSize']),
    cinemaMode: state.getIn(['application', 'cinemaMode']),
    product: state.getIn(['application', 'product']),
    isLoggedIn: state.getIn(['user', 'isLoggedIn'], false),
    virtualsAllowed: state.getIn(['user', 'virtualsAllowed']),
    casinoVisibilityRestricted: state.getIn(['vegas', 'visibilityRestricted'], true),
    navigationVisible: state.getIn(['navigation', 'navigationVisible']),
    sidebarContent: state.getIn(['local', 'sidebar', 'content']),
    sidebarVisible: state.getIn(['local', 'sidebar', 'visible'], ''),
    additionalDisplayedVerticals: state.getIn(['application', 'additionalDisplayedVerticals']),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Header));
